import type { FeedDistanceFilter, FeedTimeWindow } from '@/features/events/feed/feedPreferences';
import type { FeedPreset } from '@/features/events/feed/eventsFeedShared';
import { getDeviceTimeZone } from '@/lib/utils';
import {
  FeedItemTypeEvent,
  type FeedEvent,
  type FeedItem,
  type FeedResponse,
  type EntryType,
} from '@/lib/api/apiTypes';
import { appendEventFeedFilters } from '@/features/events/eventMobileHelpers';

export type FeedMode = 'proximity' | 'smart';

export const FEED_LIMIT = 20;
export const DEFAULT_RADIUS_METERS: FeedDistanceFilter = 16000;
export const DISCOVER_RADIUS_METERS: FeedDistanceFilter = 80000;

export const nearbyFeedKeys = {
  all: ['nearby-feed'] as const,
  list: (params: {
    latitude: number | null;
    longitude: number | null;
    originLatitude: number;
    originLongitude: number;
    radiusMeters: FeedDistanceFilter;
    timeWindow: FeedTimeWindow;
    mode: FeedMode;
    entryType?: EntryType | null;
    tagGroup?: FeedPreset;
    attributes?: string[];
    maxPriceCents?: number | null;
    viewerKey: string;
  }) => ['nearby-feed', params] as const,
};

export const followingFeedKeys = {
  all: ['following-feed'] as const,
  list: (params: { originLatitude: number; originLongitude: number }) =>
    ['following-feed', 'list', params] as const,
};

export const dashboardKeys = {
  all: ['dashboard'] as const,
  current: () => ['dashboard', 'current'] as const,
};

export function buildFeedPath({
  latitude,
  longitude,
  originLatitude,
  originLongitude,
  radiusMeters,
  timeWindow,
  limit,
  offset,
  mode,
  entryType,
  tagGroup = 'all',
  attributes = [],
  maxPriceCents = null,
}: {
  latitude?: number | null;
  longitude?: number | null;
  originLatitude?: number | null;
  originLongitude?: number | null;
  radiusMeters: FeedDistanceFilter;
  timeWindow: FeedTimeWindow;
  limit: number;
  offset: number;
  mode: FeedMode;
  entryType?: EntryType | null;
  tagGroup?: FeedPreset;
  attributes?: string[];
  maxPriceCents?: number | null;
}) {
  const params = new URLSearchParams({
    tz: getDeviceTimeZone(),
    radius: String(radiusMeters),
    time_window: timeWindow,
    limit: String(limit),
    offset: String(offset),
  });
  if (typeof latitude === 'number' && typeof longitude === 'number') {
    params.set('lat', String(latitude));
    params.set('lng', String(longitude));
  }
  if (typeof originLatitude === 'number' && typeof originLongitude === 'number') {
    params.set('origin_lat', String(originLatitude));
    params.set('origin_lng', String(originLongitude));
  }
  if (mode === 'smart') {
    params.set('mode', 'smart');
  }
  appendEventFeedFilters(params, entryType, attributes);
  if (tagGroup !== 'all') {
    params.set('tag_group', tagGroup);
  }
  if (typeof maxPriceCents === 'number') {
    params.set('max_price', String(maxPriceCents));
  }

  return `/feed?${params.toString()}`;
}

export function buildFollowingFeedPath({
  limit,
  offset,
  originLatitude,
  originLongitude,
}: {
  limit: number;
  offset: number;
  originLatitude?: number | null;
  originLongitude?: number | null;
}) {
  const params = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
    tz: getDeviceTimeZone(),
  });
  if (typeof originLatitude === 'number' && typeof originLongitude === 'number') {
    params.set('origin_lat', String(originLatitude));
    params.set('origin_lng', String(originLongitude));
  }
  return `/feed/following?${params.toString()}`;
}

export function flattenNearbyFeed(pages: FeedResponse[] | undefined) {
  return flattenFeedPages(pages?.map((page) => page.events) ?? []);
}

export function flattenNearbyFeedItems(pages: FeedResponse[] | undefined) {
  const merged: FeedItem[] = [];
  const seen = new Set<string>();

  for (const page of pages ?? []) {
    const items = page.items?.length
      ? page.items
      : page.events.map(
          (event): FeedItem => ({
            type: FeedItemTypeEvent,
            event,
            deal: null,
          })
        );

    for (const item of items) {
      const id = item.event?.id ?? item.deal?.deal.id;
      if (!id || seen.has(id)) {
        continue;
      }
      seen.add(id);
      merged.push(item);
    }
  }

  return merged;
}

export function flattenFollowingFeed(pages: FeedEvent[][] | undefined) {
  return flattenFeedPages(pages ?? []);
}

function flattenFeedPages(pages: FeedEvent[][]) {
  const merged: FeedEvent[] = [];
  const seen = new Set<string>();

  for (const page of pages) {
    for (const event of page) {
      if (seen.has(event.id)) {
        continue;
      }
      seen.add(event.id);
      merged.push(event);
    }
  }

  return merged;
}

export function applyRsvpMutation(event: FeedEvent, eventID: string, status: 'going') {
  if (event.id !== eventID) {
    return event;
  }

  const next = {
    ...event,
    user_has_rsvpd: true,
    viewer_rsvp_status: status,
  };

  if (event.viewer_rsvp_status === status) {
    return next;
  }

  const increment = event.viewer_rsvp_status === 'going' ? 0 : 1;

  return {
    ...next,
    going_count: event.going_count + increment,
    interested_count:
      event.viewer_rsvp_status === 'interested'
        ? Math.max(0, event.interested_count - 1)
        : event.interested_count,
  };
}

export function applySavedMutation(event: FeedEvent, eventID: string, isSaved: boolean) {
  if (event.id !== eventID) {
    return event;
  }

  return {
    ...event,
    is_saved: isSaved,
  };
}

export function applyRsvpRemoval(event: FeedEvent, eventID: string) {
  if (event.id !== eventID) {
    return event;
  }

  return {
    ...event,
    viewer_rsvp_status: '',
    user_has_rsvpd: false,
    going_count:
      event.viewer_rsvp_status === 'going' ? Math.max(0, event.going_count - 1) : event.going_count,
    interested_count:
      event.viewer_rsvp_status === 'interested'
        ? Math.max(0, event.interested_count - 1)
        : event.interested_count,
  };
}
